"use client"

import * as React from "react"
import {
  EmptyCell,
  NumericCell,
  PillCell,
  ProgressCell,
  StatusCell,
  type RowActionDef,
} from "@/components/data-views"
import { Button } from "@/components/ui/button"
import type { StatusBadgeSemanticSize } from "@exxatdesignux/ui/components/ui/status-badge"
import { termTimelineStatusMeta } from "@/lib/evaluation-term-utils"
import {
  evaluationAnalyticsPath,
  evaluationSchedulePath,
  evaluationTermDetailsPath,
  evaluationTermSurveysPath,
  prismCoursesPath,
  workQueueActionPath,
} from "@/lib/evaluation-routes"
import {
  evaluationCoverageCount,
  evaluationCoveragePercent,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import type { EvaluationWorkQueueRow } from "@/lib/mock/evaluation-work-queue"
import type { Product } from "@/stores/app-store"

const WORK_QUEUE_STATE_TONE: Record<
  EvaluationWorkQueueRow["state"],
  "neutral" | "info" | "warning" | "success" | "danger"
> = {
  notSetup: "warning",
  draft: "neutral",
  scheduled: "info",
  live: "success",
  closed: "neutral",
  review: "info",
  attention: "warning",
}

const WORK_QUEUE_STATE_ICON: Record<EvaluationWorkQueueRow["state"], string> = {
  notSetup: "fa-clipboard-list",
  draft: "fa-file-pen",
  scheduled: "fa-calendar-clock",
  live: "fa-signal-stream",
  closed: "fa-inbox",
  review: "fa-eye",
  attention: "fa-triangle-exclamation",
}

export function TermIdentityCell({ term }: { term: EvaluationTerm }) {
  return (
    <span className="truncate text-sm font-medium text-foreground">
      {term.academicYear} · {term.termName}
    </span>
  )
}

export function TermTimelineStatusCell({ term }: { term: EvaluationTerm }) {
  const meta = termTimelineStatusMeta(term.timelinePosition)
  return <StatusCell label={meta.label} tone={meta.tone} icon={meta.icon} />
}

export function TermPublishStatusCell({ term }: { term: EvaluationTerm }) {
  if (term.cardCase === "all-published") {
    return <StatusCell label="Published" tone="success" icon="fa-circle-check" />
  }
  if (term.cardCase === "all-closed") {
    return <StatusCell label="Closed" tone="neutral" icon="fa-inbox" />
  }
  if (term.cardCase === "no-courses") {
    return <StatusCell label="No courses" tone="warning" icon="fa-book" />
  }
  if (term.cardCase === "no-evaluations") {
    return <StatusCell label="Ready to schedule" tone="info" icon="fa-calendar-plus" />
  }
  return <PillCell label="In progress" icon="fa-layer-group" />
}

export function TermCoverageCell({ term }: { term: EvaluationTerm }) {
  if (term.courseOfferings === 0) return <EmptyCell />
  const percent = evaluationCoveragePercent(term.counts, term.courseOfferings)
  const count = evaluationCoverageCount(term.counts)
  return (
    <ProgressCell
      value={percent}
      tone="info"
      label={`${count}/${term.courseOfferings}`}
    />
  )
}

export function TermResponseCell({ term }: { term: EvaluationTerm }) {
  const rate =
    term.timelinePosition === "current" && term.liveResponseRate != null
      ? term.liveResponseRate
      : term.closedResponseRate
  if (rate == null) return <EmptyCell />
  return <ProgressCell value={rate} tone="info" label={`${rate}%`} />
}

export function TermRatingCell({ value }: { value: number | null | undefined }) {
  if (value == null) return <EmptyCell />
  return <NumericCell value={value} fractionDigits={1} />
}

export function buildTermRowActions(
  product: Product,
  onNavigate: (path: string) => void,
  term: EvaluationTerm,
): RowActionDef<EvaluationTerm>[] {
  const actions: RowActionDef<EvaluationTerm>[] = []

  if (term.ratings || term.cardCase === "all-closed" || term.cardCase === "all-published") {
    actions.push({
      label: "View analytics",
      icon: "fa-chart-line",
      onSelect: row => onNavigate(evaluationAnalyticsPath(product, row.id)),
    })
  } else if (term.cardCase === "no-courses") {
    actions.push({
      label: "Add courses",
      icon: "fa-plus",
      onSelect: () => onNavigate(prismCoursesPath()),
    })
  } else if (term.cardCase === "no-evaluations") {
    actions.push({
      label: "Schedule evaluations",
      icon: "fa-calendar-plus",
      onSelect: row =>
        onNavigate(evaluationSchedulePath(product, { termId: row.id, stages: "notSetup,draft" })),
    })
  } else {
    actions.push({
      label: "View details",
      icon: "fa-arrow-up-right-from-square",
      onSelect: row => onNavigate(evaluationTermDetailsPath(product, row.id)),
    })
  }

  actions.push({
    label: "View surveys",
    icon: "fa-list",
    onSelect: row => onNavigate(evaluationTermSurveysPath(product, row.id)),
  })

  return actions
}

/** Fits the longest label ("Schedule evaluations") at `size="sm"` + standard cell `px-3`. Use key `action`, not `actions`, so DataTable does not apply control-column padding. */
export const EVAL_TABLE_ACTION_COLUMN_SINGLE = {
  width: 168,
  minWidth: 168,
  resizable: false,
  defaultPin: "right" as const,
  lockPin: true,
} as const

/** Same width as single — stacked buttons are `w-fit`, not full cell width. */
export const EVAL_TABLE_ACTION_COLUMN_MULTI = {
  width: 168,
  minWidth: 168,
  resizable: false,
  defaultPin: "right" as const,
  lockPin: true,
} as const

function defaultRowActionVariant<TRow>(
  action: RowActionDef<TRow>,
  _index: number,
  actions: RowActionDef<TRow>[],
): React.ComponentProps<typeof Button>["variant"] {
  if (action.variant === "destructive") return "destructive-outline"
  if (actions.length > 1) return "outline"
  return "default"
}

function EvaluationTableRowActions<TRow>({
  row,
  actions,
  resolveVariant,
}: {
  row: TRow
  actions: RowActionDef<TRow>[]
  resolveVariant?: (
    action: RowActionDef<TRow>,
    index: number,
    actions: RowActionDef<TRow>[],
  ) => React.ComponentProps<typeof Button>["variant"]
}) {
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      {actions.map((action, index) => (
        <Button
          key={action.label}
          type="button"
          size="sm"
          variant={
            resolveVariant?.(action, index, actions) ??
            defaultRowActionVariant(action, index, actions)
          }
          disabled={action.disabled}
          className="w-fit shrink-0 whitespace-nowrap focus-visible:ring-inset"
          onClick={event => {
            event.stopPropagation()
            action.onSelect(row)
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  )
}

export function TermRowActionsCell({
  term,
  product,
  onNavigate,
}: {
  term: EvaluationTerm
  product: Product
  onNavigate: (path: string) => void
}) {
  return (
    <EvaluationTableRowActions
      row={term}
      actions={buildTermRowActions(product, onNavigate, term)}
    />
  )
}

export function WorkQueueStateCell({
  row,
  size,
}: {
  row: EvaluationWorkQueueRow
  size?: StatusBadgeSemanticSize
}) {
  return (
    <StatusCell
      label={row.stateLabel}
      tone={WORK_QUEUE_STATE_TONE[row.state]}
      icon={WORK_QUEUE_STATE_ICON[row.state]}
      size={size}
    />
  )
}

export function WorkQueueResponseCell({ row }: { row: EvaluationWorkQueueRow }) {
  if (row.responseRate == null) return <EmptyCell />
  return <ProgressCell value={row.responseRate} tone="info" label={`${row.responseRate}%`} />
}

const COMPACT_ACTION_LABEL: Record<EvaluationWorkQueueRow["state"], string> = {
  notSetup: "Schedule",
  draft: "Finish",
  scheduled: "Update",
  live: "Remind",
  closed: "Review",
  review: "Review",
  attention: "Review",
}

export function WorkQueueRowActionsCell({
  row,
  product,
  onNavigate,
  onAction,
  compact = false,
}: {
  row: EvaluationWorkQueueRow
  product?: Product
  onNavigate?: (path: string) => void
  /** Opens work queue sheet instead of routing (paired compact list). */
  onAction?: () => void
  /** Paired column: xs buttons with short labels. */
  compact?: boolean
}) {
  const variant = row.priority <= 1 ? "default" : "outline"
  const label = compact ? COMPACT_ACTION_LABEL[row.state] : row.actionLabel

  return (
    <Button
      type="button"
      size={compact ? "xs" : "sm"}
      variant={variant}
      aria-label={compact ? row.actionLabel : undefined}
      className="w-fit shrink-0 whitespace-nowrap focus-visible:ring-inset"
      onClick={event => {
        event.stopPropagation()
        if (onAction) {
          onAction()
          return
        }
        if (product && onNavigate) {
          onNavigate(workQueueActionPath(product, row))
        }
      }}
    >
      {label}
    </Button>
  )
}
